import React, { useEffect, useRef } from 'react';

interface ConsoleOutputProps {
    output: string;
    isOpen: boolean;
}

function ConsoleOutput({ output, isOpen }: ConsoleOutputProps) {
    const scrollRef = useRef<HTMLDivElement>(null);

    const lines = output ? output.split('\n') : [];

    // Auto-scroll to the bottom when output changes
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [output]);

    if (!isOpen) {
        return null;
    }

    return (
        <div className="overflow-hidden rounded-md border border-gray-700 shadow-lg">
            {/* Console Header */}
            <div className="flex items-center gap-2 bg-gray-800 px-3 py-1.5">
                <div className="flex items-center gap-1.5">
                    <span className="inline-block h-3 w-3 rounded-full bg-red-500 opacity-80" />
                    <span className="inline-block h-3 w-3 rounded-full bg-yellow-500 opacity-80" />
                    <span className="inline-block h-3 w-3 rounded-full bg-green-500 opacity-80" />
                </div>
                <span className="ml-2 select-none text-xs font-semibold tracking-wide text-gray-400">
                    Console Output
                </span>
            </div>

            {/* Console Body */}
            <div
                ref={scrollRef}
                className="max-h-[300px] min-h-[80px] overflow-auto bg-gray-950 p-3 font-mono text-sm leading-relaxed"
            >
                {lines.length === 0 ? (
                    <span className="animate-pulse text-gray-600">
                        Waiting for output...
                    </span>
                ) : (
                    lines.map((line, index) => (
                        <div key={index} className="flex hover:bg-gray-900">
                            <span className="mr-3 inline-block w-8 select-none text-right text-gray-600">
                                {index + 1}
                            </span>
                            <span className="whitespace-pre-wrap break-all text-green-400">
                                {line || '\u00A0'}
                            </span>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}

export default ConsoleOutput;
